// Privacy rules for analytics. Events carry ids, enums, counts, durations and
// booleans only: never a rupee figure, quantity, price, symbol or email.
// Recordings mask all text by default; `keepsText` is the only exception.
import { isDesk, type Viewer } from '@alphadesk/web/contract.ts';
import { recommendationOfPath } from '@alphadesk/web/links.ts';

// Marks text as chrome that recordings may show. Not PostHog's `ph-no-capture`,
// which means the opposite.
export const KEEP = 'ph-keep';

// Not sent: `price` is a real exit price, and ids would explode `$pathname` cardinality.
const PRIVATE_QUERY_KEYS = ['recommendation', 'position', 'ticket', 'price', 'signin'] as const;

// The desk is excluded on purpose. `member:<id>` matches `viewKey` in src/web/socket.ts
// so a recording joins to server logs.
export function personOf(viewer: Viewer): string | null {
  if (isDesk(viewer) || viewer.memberId === null) return null;
  return `member:${viewer.memberId}`;
}

// Never name, email or budget.
export function traitsOf(viewer: Viewer): Record<string, unknown> {
  return {
    memberId: viewer.memberId,
    books: viewer.books.length,
    follows: viewer.follows.length,
    analyst: viewer.analyst?.status ?? null,
  };
}

export const normalizePath = (path: string): string =>
  recommendationOfPath(path) === null ? path : '/r/:id';

// Non-URL strings pass through untouched: PostHog puts a literal `$direct` in `$referrer`.
export function scrubUrl(raw: string): string {
  const relative = raw.startsWith('/');
  let url: URL;
  try {
    url = new URL(raw, relative ? 'http://localhost' : undefined);
  } catch {
    return raw;
  }
  for (const key of PRIVATE_QUERY_KEYS) url.searchParams.delete(key);
  url.pathname = normalizePath(url.pathname);
  return relative ? `${url.pathname}${url.search}` : url.href;
}

const URL_PROPERTIES = [
  '$current_url',
  '$pathname',
  '$referrer',
  '$initial_current_url',
  '$initial_pathname',
  '$initial_referrer',
] as const;

// Runs on every event, including PostHog's own, which is where a URL would leak.
export function scrubProperties(properties: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...properties };
  for (const key of URL_PROPERTIES) {
    if (typeof out[key] === 'string') out[key] = scrubUrl(out[key]);
    const set = out.$set;
    if (set && typeof set === 'object' && typeof (set as Record<string, unknown>)[key] === 'string')
      (set as Record<string, unknown>)[key] = scrubUrl(
        (set as Record<string, unknown>)[key] as string,
      );
    const once = out.$set_once;
    if (
      once &&
      typeof once === 'object' &&
      typeof (once as Record<string, unknown>)[key] === 'string'
    )
      (once as Record<string, unknown>)[key] = scrubUrl(
        (once as Record<string, unknown>)[key] as string,
      );
  }
  return out;
}

export function keepsText(text: string, element?: Element | null): string {
  return element?.closest(`.${KEEP}`) ? text : '*'.repeat(text.length);
}

// `title`/`aria-label` carry figures (e.g. Stat titles); other attributes are needed to render.
export function keepsAttribute(name: string, value: string): string {
  return name === 'title' || name === 'aria-label' ? '*'.repeat(value.length) : value;
}

export type UsageEvent =
  | 'action taken'
  | 'action refused'
  | 'ticket opened'
  | 'ticket closed'
  | 'broker linked'
  | 'broker refused'
  | 'broker login opened'
  | 'desk read opened'
  | 'telegram linked'
  | 'feedback sent'
  | 'invite shared'
  | 'socket dropped'
  | 'signed out';
